import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  Badge,
  PageContainer,
  PageHeader,
  PageHeaderLeading,
  PageHeaderStack,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@yeshuman/sdk'
import { History } from 'lucide-react'

import { PlatformAreaError, usePlatformActionLog } from '../../hooks/use-platform-area'
import {
  ActionStatusBadge,
  IncludeSimulatedSwitch,
  ListPager,
  PLATFORM_PAGE_SIZE,
  PlatformOnlyNotice,
  SimulatedBadge,
} from './platform-area-parts'

const TARGET_LIST: Record<string, { label: string; path: string }> = {
  job: { label: 'Job', path: '/platform/jobs' },
  profile: { label: 'Profile', path: '/platform/profiles' },
}

export function PlatformActionsPage() {
  const [params, setParams] = useSearchParams()
  const targetType = params.get('target_type') ?? ''
  const targetId = params.get('target_id') ?? ''
  const action = params.get('action') ?? ''
  const [includeSimulated, setIncludeSimulated] = useState(Boolean(targetId))
  const [offset, setOffset] = useState(0)

  useEffect(() => setOffset(0), [targetType, targetId, action, includeSimulated])

  const { data, isLoading, error } = usePlatformActionLog({
    targetType,
    targetId,
    action,
    includeSimulated,
    offset,
    limit: PLATFORM_PAGE_SIZE,
  })

  if (error instanceof PlatformAreaError && (error.status === 401 || error.status === 403)) {
    return <PlatformOnlyNotice title="Platform action log" />
  }

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params)
    if (value) next.set(key, value)
    else next.delete(key)
    if (key === 'target_type') next.delete('target_id')
    setParams(next, { replace: true })
  }

  const total = data?.total ?? 0
  const focusLabel = targetId ? data?.results[0]?.target_label : ''
  const back = TARGET_LIST[targetType]

  return (
    <PageContainer className="space-y-6">
      <PageHeaderStack>
        <PageHeader
          leading={
            back && targetId ? (
              <PageHeaderLeading back={{ label: `Back to ${back.label.toLowerCase()}s`, to: back.path }} />
            ) : (
              <PageHeaderLeading />
            )
          }
          title={focusLabel ? `Action history: ${focusLabel}` : 'Platform action log'}
          description="Every action a Platform user ran on a job or candidate profile, with who ran it and what happened."
          icon={<History className="h-5 w-5" />}
        />
        <div className="flex flex-wrap items-center gap-4">
          <Select value={targetType || 'all'} onValueChange={(value) => setParam('target_type', value === 'all' ? '' : value)}>
            <SelectTrigger className="w-[140px]" aria-label="Record">
              <SelectValue placeholder="Record" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="job">Jobs</SelectItem>
              <SelectItem value="profile">Profiles</SelectItem>
            </SelectContent>
          </Select>
          <Select value={action || 'all'} onValueChange={(value) => setParam('action', value === 'all' ? '' : value)}>
            <SelectTrigger className="w-[220px]" aria-label="Action">
              <SelectValue placeholder="Action" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {(data?.actions ?? []).map((choice) => (
                <SelectItem key={choice.value} value={choice.value}>
                  {choice.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <IncludeSimulatedSwitch
            id="platform-actions-simulated"
            checked={includeSimulated}
            onCheckedChange={setIncludeSimulated}
          />
          {targetId ? (
            <Link
              to={`/platform/actions${targetType ? `?target_type=${targetType}` : ''}`}
              className="text-sm text-primary underline-offset-4 hover:underline"
            >
              Show all {targetType ? `${targetType}s` : 'records'}
            </Link>
          ) : null}
          <p className="text-sm text-muted-foreground" role="status">
            {isLoading ? 'Loading…' : `${total} action${total === 1 ? '' : 's'}`}
          </p>
        </div>
      </PageHeaderStack>

      {error && !(error instanceof PlatformAreaError && error.status < 500) ? (
        <p className="text-sm text-destructive">{(error as Error).message}</p>
      ) : null}

      {isLoading ? <Skeleton className="h-64 w-full" /> : null}

      {!isLoading && !error && total === 0 ? (
        <p className="text-sm text-muted-foreground">No Platform actions yet.</p>
      ) : null}

      {data && data.results.length > 0 ? (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Record</TableHead>
                <TableHead>Outcome</TableHead>
                <TableHead>By</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.results.map((run) => (
                <TableRow key={run.id}>
                  <TableCell className="align-top whitespace-nowrap text-sm tabular-nums text-muted-foreground">
                    {new Date(run.started_at).toLocaleString()}
                  </TableCell>
                  <TableCell className="align-top text-sm">{run.action_label}</TableCell>
                  <TableCell className="align-top">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="outline">{TARGET_LIST[run.target_type]?.label ?? run.target_type}</Badge>
                      <Link
                        to={`/platform/actions?target_type=${run.target_type}&target_id=${run.target_id}`}
                        className="text-sm text-primary underline-offset-4 hover:underline"
                      >
                        {run.target_label || run.target_id}
                      </Link>
                      {run.target_is_simulation ? <SimulatedBadge /> : null}
                    </div>
                  </TableCell>
                  <TableCell className="align-top">
                    <div className="space-y-1">
                      <ActionStatusBadge status={run.status} />
                      {run.summary ? <p className="max-w-md text-sm text-muted-foreground">{run.summary}</p> : null}
                    </div>
                  </TableCell>
                  <TableCell className="align-top text-sm">
                    {run.actor_name || run.actor_email || '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : null}

      <ListPager total={total} offset={offset} disabled={isLoading} onOffsetChange={setOffset} />
    </PageContainer>
  )
}
